/**
 * Zamrożony interfejs nagród mini-gier (§23.1).
 * WP12* wołają startRun/finishRun — implementacja HTTP podłączona przez WP15.
 * Nagrody idą WYŁĄCZNIE tędy; po udanym finish publikujemy saldo na balanceBus
 * (HUD odświeża licznik live).
 *
 * WAVE3-G5: offline stub przyznaje bazę lokalnie; 4xx nie maskujemy stubem
 * (pokazujemy powód); jeden retry przy nieudanym finish.
 */
import {
  MINIGAME_FINISH_BASE_ZL,
  RunStartRequestSchema,
  RunStartResponseSchema,
  RunFinishRequestSchema,
  RunFinishResponseSchema,
  type GameId,
} from '@pieskowo/shared';
import { OutfitStore } from '../avatar/outfitStore.js';
import { apiBase, authHeaders } from './apiBase.js';
import { SaveLocal } from './SaveLocal.js';
import { balanceBus } from './balanceBus.js';

export type RunStatsClient = {
  stars: number;
  score: number;
  durationMs?: number;
  taps?: number;
};

export type RunFinishResult = {
  rewardZl: number;
  balance: number;
  stars: number;
  serverDurationMs: number;
  effectiveTaps?: number;
  tapsClamped?: boolean;
  /** Komunikat PL gdy finish nie dał nagrody (4xx / błąd) — do RewardScene. */
  errorMessage?: string;
};

export interface RunClient {
  startRun(gameId: string): Promise<{ runId: string }>;
  finishRun(runId: string, stats: RunStatsClient): Promise<RunFinishResult>;
}

/** Stub offline — lokalna nagroda bazowa (dziecko widzi przyrost zł bez sieci). */
export class StubRunClient implements RunClient {
  async startRun(gameId: string): Promise<{ runId: string }> {
    return { runId: `local-${gameId}-${Date.now()}` };
  }
  async finishRun(
    _runId: string,
    stats: RunStatsClient,
  ): Promise<RunFinishResult> {
    const rewardZl = MINIGAME_FINISH_BASE_ZL;
    const prev = OutfitStore.load().balanceZl;
    const balance = prev + rewardZl;
    // saveBalance → localStorage + balanceBus (HUD)
    OutfitStore.saveBalance(balance);
    return {
      rewardZl,
      balance,
      stars: stats.stars,
      serverDurationMs: stats.durationMs ?? 0,
      tapsClamped: false,
    };
  }
}

const stub = new StubRunClient();

/** Online tylko z prawdziwym tokenem sesji (nie offline-/pusty). */
function online(): boolean {
  const t = SaveLocal.getToken();
  if (!t || t.startsWith('offline-')) return false;
  return true;
}

function polishErrorForStatus(status: number): string {
  if (status === 401 || status === 403) {
    return 'Zaloguj się, żeby zbierać złotówki!';
  }
  if (status === 429) {
    return 'Chwileczkę — za dużo gier pod rząd. Spróbuj za moment!';
  }
  if (status >= 500) {
    return 'Serwer chwilowo niedostępny. Spróbuj jeszcze raz!';
  }
  // 4xx (walidacja itd.)
  return 'Nie udało się zapisać nagrody. Spróbuj jeszcze raz!';
}

/**
 * HTTP RunClient — /api/runs/start + /api/runs/finish (kontrakt runs §7.2).
 * Brak tokena → stub z lokalną nagrodą bazową.
 * Błąd sieci po retry → stub (offline).
 * 4xx po retry → wynik z errorMessage (BEZ cichego stuba).
 */
export class HttpRunClient implements RunClient {
  async startRun(gameId: string): Promise<{ runId: string }> {
    if (!online()) return stub.startRun(gameId);
    const parsed = RunStartRequestSchema.safeParse({ gameId: gameId as GameId });
    if (!parsed.success) return stub.startRun(gameId);
    try {
      const res = await fetch(`${apiBase()}/api/runs/start`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(parsed.data),
      });
      if (!res.ok) return stub.startRun(gameId);
      const json: unknown = await res.json();
      const out = RunStartResponseSchema.safeParse(json);
      return out.success ? { runId: out.data.runId } : stub.startRun(gameId);
    } catch {
      return stub.startRun(gameId);
    }
  }

  async finishRun(
    runId: string,
    stats: RunStatsClient,
  ): Promise<RunFinishResult> {
    if (!online()) return stub.finishRun(runId, stats);
    const parsed = RunFinishRequestSchema.safeParse({ runId, stats });
    if (!parsed.success) return stub.finishRun(runId, stats);
    return this.finishWithRetry(parsed.data, stats, /* attempt */ 0);
  }

  private async finishWithRetry(
    body: { runId: string; stats: RunStatsClient },
    stats: RunStatsClient,
    attempt: number,
  ): Promise<RunFinishResult> {
    const maxAttempts = 2; // pierwszy + jeden retry
    try {
      const res = await fetch(`${apiBase()}/api/runs/finish`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        if (attempt + 1 < maxAttempts) {
          return this.finishWithRetry(body, stats, attempt + 1);
        }
        // 4xx/5xx final — NIE maskuj stubem (dziecko ma zobaczyć powód, nie fałszywe +5)
        const msg = polishErrorForStatus(res.status);
        console.warn('[RunClient] finishRun failed', res.status, msg);
        return {
          rewardZl: 0,
          balance: OutfitStore.load().balanceZl,
          stars: stats.stars,
          serverDurationMs: stats.durationMs ?? 0,
          errorMessage: msg,
        };
      }
      const json: unknown = await res.json();
      const out = RunFinishResponseSchema.safeParse(json);
      if (!out.success) {
        if (attempt + 1 < maxAttempts) {
          return this.finishWithRetry(body, stats, attempt + 1);
        }
        console.warn('[RunClient] finishRun bad response shape');
        return {
          rewardZl: 0,
          balance: OutfitStore.load().balanceZl,
          stars: stats.stars,
          serverDurationMs: stats.durationMs ?? 0,
          errorMessage: 'Nie udało się zapisać nagrody. Spróbuj jeszcze raz!',
        };
      }
      // saldo autorytatywne → HUD live + lokalny cache
      balanceBus.set(out.data.balance);
      OutfitStore.saveBalance(out.data.balance);
      return { ...out.data };
    } catch {
      if (attempt + 1 < maxAttempts) {
        return this.finishWithRetry(body, stats, attempt + 1);
      }
      // sieć padła → tryb offline (lokalna baza)
      console.warn('[RunClient] finishRun network error — offline stub');
      return stub.finishRun(body.runId, stats);
    }
  }
}

export const runClient: RunClient = new HttpRunClient();
